import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Avatar,
  Menu,
  ActionIcon,
  Progress,
  Tooltip,
} from '@mantine/core';
import { IconDots, IconEdit, IconTrash, IconEye } from '@tabler/icons-react';
import { Card } from '../../ui';
import { HoverLift, FadeIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface EntityCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  image?: string;
  avatar?: string;
  avatarName?: string;
  status?: {
    label: string;
    color: string;
  };
  badges?: {
    label: string;
    color?: string;
  }[];
  progress?: {
    value: number;
    label?: string;
    color?: string;
  };
  metadata?: {
    label: string;
    value: string | number;
    icon?: ReactNode;
  }[];
  actions?: {
    onView?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    custom?: {
      key: string;
      label: string;
      icon?: ReactNode;
      onClick: () => void;
      color?: string;
    }[];
  };
  onClick?: () => void;
  selected?: boolean;
  selectable?: boolean;
  onSelect?: (selected: boolean) => void;
  footer?: ReactNode;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  variant?: 'default' | 'compact' | 'horizontal';
  className?: string;
}

export const EntityCard = forwardRef<HTMLDivElement, EntityCardProps>(
  (
    {
      title,
      subtitle,
      description,
      image,
      avatar,
      avatarName,
      status,
      badges = [],
      progress,
      metadata = [],
      actions,
      onClick,
      selected = false,
      selectable = false,
      onSelect,
      footer,
      gradientVariant,
      variant = 'default',
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const hasActions = actions?.onView || actions?.onEdit || actions?.onDelete || actions?.custom?.length;

    const renderActionsMenu = () => (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            onClick={(e) => e.stopPropagation()}
            size="sm"
          >
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {actions?.onView && (
            <Menu.Item leftSection={<IconEye size={14} />} onClick={actions.onView}>
              View
            </Menu.Item>
          )}
          {actions?.onEdit && (
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={actions.onEdit}>
              Edit
            </Menu.Item>
          )}
          {actions?.custom?.map((action) => (
            <Menu.Item
              key={action.key}
              leftSection={action.icon}
              color={action.color}
              onClick={action.onClick}
            >
              {action.label}
            </Menu.Item>
          ))}
          {actions?.onDelete && (
            <>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={actions.onDelete}
              >
                Delete
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    );

    if (variant === 'horizontal') {
      return (
        <FadeIn>
          <HoverLift lift={4}>
            <Card
              ref={ref}
              className={className}
              onClick={onClick}
              style={{
                cursor: onClick ? 'pointer' : 'default',
                borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
                borderWidth: selected ? 2 : 1,
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group wrap="nowrap">
                  {(image || avatar) && (
                    <Avatar src={image || avatar} size="lg" radius="md">
                      {avatarName?.charAt(0)}
                    </Avatar>
                  )}
                  <Box>
                    <Group gap="xs">
                      <Text fw={600} lineClamp={1} style={accessibilityStyles}>{title}</Text>
                      {status && <Badge color={status.color} size="sm">{status.label}</Badge>}
                    </Group>
                    {subtitle && <Text size="sm" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>}
                  </Box>
                </Group>
                <Group gap="md" wrap="nowrap">
                  {metadata.slice(0, 3).map((item, index) => (
                    <Tooltip key={index} label={item.label}>
                      <Group gap={4}>
                        {item.icon}
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>{item.value}</Text>
                      </Group>
                    </Tooltip>
                  ))}
                  {hasActions && renderActionsMenu()}
                </Group>
              </Group>
            </Card>
          </HoverLift>
        </FadeIn>
      );
    }

    if (variant === 'compact') {
      return (
        <FadeIn>
          <HoverLift lift={4}>
            <Card
              ref={ref}
              className={className}
              p="sm"
              onClick={onClick}
              style={{
                cursor: onClick ? 'pointer' : 'default',
                borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  {avatar && (
                    <Avatar src={avatar} size="sm" radius="xl">
                      {avatarName?.charAt(0)}
                    </Avatar>
                  )}
                  <Box>
                    <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>{title}</Text>
                    {subtitle && <Text size="xs" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>}
                  </Box>
                </Group>
                <Group gap="xs">
                  {status && <Badge color={status.color} size="xs">{status.label}</Badge>}
                  {hasActions && renderActionsMenu()}
                </Group>
              </Group>
            </Card>
          </HoverLift>
        </FadeIn>
      );
    }

    // Default variant
    return (
      <FadeIn>
        <HoverLift lift={6}>
          <Card
            ref={ref}
            className={className}
            onClick={onClick}
            style={{
              cursor: onClick ? 'pointer' : 'default',
              borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
              borderWidth: selected ? 2 : 1,
            }}
          >
            <Stack gap="sm">
              {/* Image */}
              {image && (
                <Box
                  style={{
                    height: 160,
                    margin: '-16px -16px 0 -16px',
                    backgroundImage: `url(${image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: '8px 8px 0 0',
                  }}
                />
              )}

              {/* Header */}
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>
                  {avatar && !image && (
                    <Avatar src={avatar} size="md" radius="xl">
                      {avatarName?.charAt(0)}
                    </Avatar>
                  )}
                  <Box style={{ overflow: 'hidden' }}>
                    <Text fw={600} lineClamp={1} style={accessibilityStyles}>{title}</Text>
                    {subtitle && (
                      <Text size="sm" c="dimmed" lineClamp={1} style={accessibilityStyles}>{subtitle}</Text>
                    )}
                  </Box>
                </Group>
                {hasActions && renderActionsMenu()}
              </Group>

              {/* Status and badges */}
              {(status || badges.length > 0) && (
                <Group gap="xs">
                  {status && <Badge color={status.color}>{status.label}</Badge>}
                  {badges.map((badge, index) => (
                    <Badge key={index} color={badge.color} variant="light">
                      {badge.label}
                    </Badge>
                  ))}
                </Group>
              )}

              {/* Description */}
              {description && (
                <Text size="sm" c="dimmed" lineClamp={2} style={accessibilityStyles}>
                  {description}
                </Text>
              )}

              {/* Progress */}
              {progress && (
                <Box>
                  <Group justify="space-between" mb={4}>
                    {progress.label && <Text size="xs" c="dimmed" style={accessibilityStyles}>{progress.label}</Text>}
                    <Text size="xs" fw={500} style={accessibilityStyles}>{progress.value}%</Text>
                  </Group>
                  <Progress value={progress.value} color={progress.color} size="sm" />
                </Box>
              )}

              {/* Metadata */}
              {metadata.length > 0 && (
                <Group gap="md">
                  {metadata.map((item, index) => (
                    <Group key={index} gap={4}>
                      {item.icon}
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>
                        {item.label}: <Text component="span" fw={500} style={accessibilityStyles}>{item.value}</Text>
                      </Text>
                    </Group>
                  ))}
                </Group>
              )}

              {/* Footer */}
              {footer}
            </Stack>
          </Card>
        </HoverLift>
      </FadeIn>
    );
  }
);

EntityCard.displayName = 'EntityCard';

export default EntityCard;
